import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMeta } from "../context/MetaContext";
import { useWebsite } from "../context/WebsiteContext";
import SimpleTextEditor from "../components/SimpleTextEditor";

export default function DashboardPage() {
  const { toolCatalog } = useMeta();
  const { websites, createWebsite } = useWebsite();
  const [form, setForm] = useState({ name: "", url: "", description: "", status: "active" });
  const [saving, setSaving] = useState(false);

  const summary = useMemo(() => {
    const counts = websites.reduce(
      (acc, site) => {
        acc.total += 1;
        acc[site.status] = (acc[site.status] || 0) + 1;
        return acc;
      },
      { total: 0, active: 0, monitoring: 0, archived: 0 }
    );
    return counts;
  }, [websites]);

  const toolCards = useMemo(() => {
    return (toolCatalog || []).slice(0, 3).map((tool, index) => ({
      key: tool.slug || `tool-${index}`,
      title: tool.name || "Configured Tool",
      permalink: tool.permalink || "/",
      detail: tool.summary || "Tool metadata is configured from database.",
      cta: tool.slug === "wpa" ? "Launch Workspace" : "Open Landing",
      href: tool.slug === "wpa" ? "/tools/wpa/dashboard" : (tool.permalink || "/")
    }));
  }, [toolCatalog]);

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await createWebsite(form);
      setForm({ name: "", url: "", description: "", status: "active" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="page-grid">
      <section className="hero-card dashboard-hero fade-in">
        <div>
          <p className="eyebrow">Global Dashboard</p>
          <h3>Multi-Tool Operations Dashboard</h3>
          <p>Use this area for global insights, tool access, and account-level setup. Website workspace selection is now under the WordPress Tool menu.</p>
        </div>
      </section>

      <section className="stats-row fade-in-delayed">
        <article className="stat-card"><p>Total Websites</p><strong>{summary.total}</strong></article>
        <article className="stat-card"><p>Active</p><strong>{summary.active}</strong></article>
        <article className="stat-card"><p>Monitoring</p><strong>{summary.monitoring}</strong></article>
        <article className="stat-card"><p>Archived</p><strong>{summary.archived}</strong></article>
      </section>

      <section className="card-modern">
        <h4>Tool Library</h4>
        <div className="tool-grid">
          {toolCards.map((tool) => (
            <article key={tool.key} className="tool-card">
              <p className="tool-permalink">{tool.permalink}</p>
              <h5>{tool.title}</h5>
              <p>{tool.detail}</p>
              <Link to={tool.href || tool.permalink} className="button-link ghost">{tool.cta}</Link>
            </article>
          ))}
          {!toolCards.length ? <p className="muted">No tool catalog entries are configured yet.</p> : null}
        </div>
      </section>

      <section className="content-two-col">
        <article className="card-modern">
          <h4>Create New Website Workspace</h4>
          <form onSubmit={submit}>
            <input required placeholder="Website name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input required placeholder="https://example.com" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
            <SimpleTextEditor placeholder="Business purpose and context" rows={4} value={form.description} onChange={(value) => setForm({ ...form, description: value })} ariaLabel="dashboard-workspace-description" />
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="active">active</option>
              <option value="monitoring">monitoring</option>
              <option value="archived">archived</option>
            </select>
            <button type="submit" disabled={saving}>{saving ? "Saving..." : "Create Website"}</button>
          </form>
        </article>

        <article className="card-modern">
          <h4>Workspace Guidance</h4>
          <ul>
            <li>Dashboard and Settings are global menus for tool-level setup.</li>
            <li>Website menus appear only after entering WordPress tool workspace.</li>
            <li>Use permalink `/tools/wpa` for the WordPress Assessment tool page.</li>
            <li>Use top global links to move between Home, tools, and settings.</li>
          </ul>
        </article>
      </section>
    </main>
  );
}
